import React, { useRef, useState } from "react";
import PropTypes from "prop-types";

import { generateFormToSubmitValues, generateFormInitialValues } from '../../utils/components';
import useFetch from '../../hooks/useFetch';
import { nestedButtonsGenerator } from '../../utils/emails';

import { withFormUtils } from '../../hoc/withFormUtils';
import { Form as FinalForm } from "react-final-form";
import { Form, Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Input } from "reactstrap";
import FormFieldTranslations from '../formFields/FormFieldTranslations';
import FormFieldTranslationsQuill from '../formFields/FormFieldTranslationsQuill';
import WarningModal from '../modals/WarningModal';

import { formUtilsHocPropTypes, stringOrNumberPropTypes } from '../../propTypes';
import FormFieldWithCheckbox from "../formFields/FormFieldWithCheckbox";
import FormFieldEmailList from "../formFields/FormFieldEmailList";
import FormFieldWithDropdown from "../formFields/FormFieldWithDropdown";
import { orderStatusAdminDefaultTemplate } from "./orderStatusAdminDefaultTemplate";
import { orderStatusClientDefaultTemplate } from "./orderStatusClientDefaultTemplate";
import { orderConfirmAdminDefaultTemplate } from "./orderConfirmAdminDefaultTemplate";
import { orderConfirmClientDefaultTemplate } from "./orderConfirmClientDefaultTemplate";

const propTypes = {
    isModalOpen: PropTypes.bool,
    setIsModalOpen: PropTypes.func,
    onSubmit: PropTypes.func,
    onSendTestEmail: PropTypes.func,
    utils: formUtilsHocPropTypes,
    translations: PropTypes.shape({
        inputs: PropTypes.shape({
            file: PropTypes.shape({
                upload: PropTypes.string,
                remove: PropTypes.string,
                edit: PropTypes.string,
            }),
            alternativeText: PropTypes.string,
            dateTo: PropTypes.string,
            dateFrom: PropTypes.string,
        }),
        actions: PropTypes.shape({
            save: PropTypes.string,
            calncel: PropTypes.string,
        })
    }),
    modalHeader: PropTypes.string,
    getEmailTemplate: PropTypes.func,
    templateId: stringOrNumberPropTypes,
};

/**
 * @param {any} template
 */
function getEmailTemplateVariables(template)
{
    try
    {
        return template?.params.map((/** @type {any} */ param) => param.value.replace('{', '').replace('}', '')) ?? [];
    }
    catch
    {
        //
    }
    return [];
}

/**
 * @param {any} template
 */
function getDefaultTemplate(template)
{
    switch (template?.emailTemplate?.id)
    {
        case 6: return orderConfirmClientDefaultTemplate;
        case 7: return orderConfirmAdminDefaultTemplate;
        case 8: return orderStatusClientDefaultTemplate;
        case 9: return orderStatusAdminDefaultTemplate;
        default: return null;
    }
}

const EditEmailTemplateModal = ({
    translations,
    isModalOpen,
    setIsModalOpen,
    onSubmit,
    onSendTestEmail,
    utils,
    getEmailTemplate,
    templateId,
    modalHeader,
}) => {
    const isFormChanged = useRef();
    const [ isWarningModalOpen, setIsWarningModalOpen ] = useState(false);
    const [ testEmail, setTestEmail ] = useState('');
    const [ orderId, setOrderId ] = useState('');
    const [ template ] = useFetch(getEmailTemplate, templateId, [templateId]);

    const config = {
        translations: ['content', 'title'],
    };

    const handleSubmit = form => {
        onSubmit(templateId, generateFormToSubmitValues(form, config, utils.allLanguages()));
        setIsModalOpen(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const toggleModal = () => {
        if (isFormChanged.current) {
            setIsWarningModalOpen(true);
        }
        else {
            setIsModalOpen(!isModalOpen);
        }
    };

    const defaultTemplate = getDefaultTemplate(template);
    const variables = getEmailTemplateVariables(template);
    const isOrderTemplate = [6, 7, 8, 9].includes(Number(template?.emailTemplate?.id));

    const handleSendTestEmail = async () => {
        if (!testEmail || !testEmail.trim()) {
            utils.toast(translations.testEmailNotDefined, 'error');
            return;
        }

        if (isOrderTemplate && (!orderId || !orderId.trim())) {
            utils.toast(translations.orderIdNotDefined, 'error');
            return;
        }

        await onSendTestEmail(templateId, {
            testEmail: testEmail.trim(),
            orderId: isOrderTemplate ? orderId.trim() : null,
        });

        utils.toast(translations.testEmailSended);
    };

    return template ? (
        <Modal isOpen={isModalOpen} toggle={toggleModal} size='full'>
            <ModalHeader toggle={toggleModal}>{modalHeader}</ModalHeader>
            <FinalForm
                onSubmit={handleSubmit}
                initialValues={generateFormInitialValues(template.emailTemplate, config, utils.allLanguages())}
                render={({ handleSubmit , form, pristine }) => {
                    isFormChanged.current = !pristine;

                    const handleCustomButtonGenerator = () =>
                    {
                        return nestedButtonsGenerator(template.params, utils.currentLanguage(), translations.insertValueButton, variables);
                    };

                    return (
                        <Form onSubmit={handleSubmit} className='w-100 p-0'>
                            <ModalBody>
                                <Row>
                                    <div className="col-6">
                                        <div className="card">
                                            <div className="card-header">{translations.sections.general}</div>
                                            <div className="card-body">
                                                <FormFieldTranslations
                                                    form={form}
                                                    modalHeader={translations.inputs.title}
                                                    propertyName='title'
                                                    currentIso={utils.currentLanguage()}
                                                    label={translations.inputs.title}
                                                />
                                                <FormFieldWithCheckbox
                                                    className="m-2"
                                                    name='sendToAdmin'
                                                    label={translations.inputs.sendToAdmin}
                                                />
                                                <FormFieldWithDropdown
                                                    label={translations.inputs.account}
                                                    name='account'
                                                    options={template.accounts}
                                                    currentSelectionLabel={template.emailTemplate.account}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="card">
                                            <div className="card-header">{translations.sections.additionalEmails}</div>
                                            <div className="card-body">
                                                <FormFieldEmailList
                                                    name='additionalEmails'
                                                    label={translations.inputs.additionalEmails}
                                                    addButtonText={translations.inputs.addButtonText}
                                                />
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="card-header">{translations.sections.testMessage}</div>
                                            <div className="card-body">
                                                <div className="mb-2">{translations.inputs.testEmail}</div>
                                                <Input
                                                    className="mb-2"
                                                    type="email"
                                                    value={testEmail}
                                                    onChange={e => setTestEmail(e.target.value)}
                                                    placeholder={translations.inputs.testEmail}
                                                />

                                                {
                                                    isOrderTemplate && (
                                                        <>
                                                            <div className="mb-2">{translations.inputs.orderId}</div>
                                                            <Input
                                                                className="mb-2"
                                                                type="text"
                                                                value={orderId}
                                                                onChange={e => setOrderId(e.target.value)}
                                                                placeholder={translations.inputs.orderNumber}
                                                            />
                                                        </>
                                                    )
                                                }

                                                <Button color="warning" type="button" onClick={handleSendTestEmail}>
                                                    {translations.actions.sendTestEmail}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Row>
                                <div>
                                    <FormFieldTranslationsQuill
                                        form={form}
                                        utils={utils}
                                        propertyName="content"
                                        header={translations.inputs.content}
                                        tinyMCEButtonGenerator={handleCustomButtonGenerator}
                                        tinyMCEVariables={variables}
                                        tinyMCEProfile={{ kind: 'raw-html' }}
                                        resetValue={defaultTemplate}
                                    />
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" type="submit">
                                    {translations.actions.save}
                                </Button>
                                <Button color="secondary" type="button" onClick={handleCancel}>
                                    {translations.actions.cancel}
                                </Button>
                            </ModalFooter>
                            <WarningModal
                                setIsOpen={setIsWarningModalOpen}
                                isOpen={isWarningModalOpen}
                                setIsMainModalOpen={setIsModalOpen}
                            />
                        </Form>
                    );
                }}
            />
        </Modal>
    ) : (
        null
    );
};

EditEmailTemplateModal.propTypes = propTypes;

export default withFormUtils(EditEmailTemplateModal);